import { SENHA_MIN_CARACTERES, violacoesDaSenha } from '@baiocchi/shared'
import { cn } from '@baiocchi/ui'
import { useMemo } from 'react'

import { IconeAlerta, IconeCheck } from './icones'

/**
 * Validação de força de senha em tempo real (TELA-00B).
 *
 * Usa `violacoesDaSenha` de `@baiocchi/shared` — a **mesma função** que o caso de
 * uso da API chama antes de devolver `422 SENHA_FRACA`. Reescrever a política em
 * regex na tela criaria duas definições, e a divergência apareceria como "a tela
 * aceitou e o servidor recusou", que é o pior lugar para descobrir a regra.
 *
 * Mostra **todas** as violações de uma vez (RNF-115): uma lista de requisitos é
 * mais rápida de satisfazer do que descobrir um erro por tentativa.
 */
export function ForcaSenha({
  senha,
  email,
  nome,
  className,
}: {
  senha: string
  email?: string | null
  nome?: string | null
  className?: string
}) {
  const violacoes = useMemo(
    () => (senha.length === 0 ? [] : violacoesDaSenha(senha, { email, nome })),
    [senha, email, nome],
  )

  const requisitos = useMemo(
    () => [
      {
        rotulo: `Pelo menos ${SENHA_MIN_CARACTERES} caracteres`,
        atendido: senha.length >= SENHA_MIN_CARACTERES,
      },
      { rotulo: 'Pelo menos uma letra', atendido: /[a-zA-Z]/.test(senha) },
      { rotulo: 'Pelo menos um número', atendido: /[0-9]/.test(senha) },
    ],
    [senha],
  )

  if (senha.length === 0) {
    return (
      <ul className={cn('flex flex-col gap-1 text-xs text-conteudo-fraco', className)}>
        {requisitos.map((requisito) => (
          <li key={requisito.rotulo} className="flex items-center gap-1.5">
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-borda-forte" />
            {requisito.rotulo}
          </li>
        ))}
      </ul>
    )
  }

  const extras = violacoes.filter(
    (violacao) =>
      violacao.codigo !== 'MIN_CARACTERES' &&
      violacao.codigo !== 'SEM_LETRA' &&
      violacao.codigo !== 'SEM_NUMERO',
  )

  const forte = violacoes.length === 0

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex gap-1" aria-hidden="true">
        {[0, 1, 2].map((indice) => {
          const atendidos = requisitos.filter((requisito) => requisito.atendido).length
          const preenchido = forte ? 3 : atendidos
          return (
            <span
              key={indice}
              className={cn(
                'h-1.5 flex-1 rounded-full transition-colors',
                indice < preenchido
                  ? forte
                    ? 'bg-sucesso'
                    : 'bg-atencao'
                  : 'bg-superficie-3',
              )}
            />
          )
        })}
      </div>

      <ul className="flex flex-col gap-1 text-xs">
        {requisitos.map((requisito) => (
          <li
            key={requisito.rotulo}
            className={cn(
              'flex items-center gap-1.5',
              requisito.atendido ? 'text-sucesso' : 'text-conteudo-fraco',
            )}
          >
            {requisito.atendido ? (
              <IconeCheck className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-borda-forte" />
            )}
            {requisito.rotulo}
          </li>
        ))}

        {extras.map((violacao) => (
          <li key={violacao.codigo} className="flex items-start gap-1.5 text-atencao">
            <IconeAlerta className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {violacao.mensagem}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** `true` quando a senha satisfaz a política inteira. */
export function senhaAceitavel(senha: string, contexto?: { email?: string | null; nome?: string | null }): boolean {
  return violacoesDaSenha(senha, contexto ?? {}).length === 0
}
